// What a homeowner sees for each referral they've sent (portal → Referrals tab).
//
// Greg (Oct 20, 2026): "the status of their referrals should be in there so
// they can track from there." Each referral shows a line in plain words and,
// while it's in play, five steps: Sent → Reached out → Inspection → Roof built
// → $250 paid.
//
// `progress` comes from ho_list_referrals and is never the job's stage name.

import type { HoReferral } from "./homeownerRpc";

export const REFERRAL_REWARD = 250;

export type ReferralProgress = NonNullable<HoReferral["progress"]>;

export const REFERRAL_STEPS = ["Sent", "Reached out", "Inspection", "Roof built", `$${REFERRAL_REWARD} paid`];

/** Where the referred job stands. A response from before Oct 20, 2026 has no
 *  `progress`, so fall back to what the older fields say. */
export function progressOf(r: HoReferral): ReferralProgress {
  if (r.progress) return r.progress;
  if (["earned", "approved", "paid"].includes(r.payout_status)) return "built";
  if (r.status === "no_thanks" || r.status === "unqualified") return "closed";
  if (r.status === "appt_scheduled") return "scheduled";
  if (r.status === "contacted") return "contacted";
  return "received";
}

/** Index into REFERRAL_STEPS of the last step reached. */
export function stepReached(r: HoReferral): number {
  if (r.payout_status === "paid") return 4;
  return { received: 0, contacted: 1, scheduled: 2, built: 3, closed: 0 }[progressOf(r)];
}

/** The steps are for referrals still in play or paid out. One that stopped
 *  (no thanks, someone else referred them first, or no reward) just says so. */
export function showsSteps(r: HoReferral): boolean {
  const p = progressOf(r);
  return !r.superseded && p !== "closed" && !(r.payout_status === "void" && p === "built");
}

/** The line under the name. Deliberately plain-spoken: if another neighbor
 *  referred them first we SAY so, rather than let someone expect a reward that
 *  is never coming. */
export function referralStatus(r: HoReferral): { label: string; cls: string } {
  const fee = `$${Number(r.payout_amount ?? REFERRAL_REWARD)}`;
  if (r.payout_status === "paid") {
    return {
      label: `✓ ${fee} reward paid${r.payout_date ? ` · ${new Date(r.payout_date + "T12:00:00").toLocaleDateString()}` : ""}`,
      cls: "bg-emerald-50 text-emerald-700 border-emerald-200",
    };
  }
  if (r.superseded) {
    return {
      label: "Someone else referred them first — no reward on this one",
      cls: "bg-slate-50 text-slate-500 border-slate-200",
    };
  }
  // Greg: the customer should see pending vs approved, not one lumped state.
  if (r.payout_status === "approved") {
    return { label: `✓ ${fee} reward approved — payment on its way`, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" };
  }
  if (r.payout_status === "earned") {
    return { label: `🎉 Their roof is built — your ${fee} reward is being approved`, cls: "bg-amber-50 text-amber-800 border-amber-200" };
  }
  const p = progressOf(r);
  if (p === "built") {
    // Built, but the fee was looked at and not paid.
    return { label: "Their roof is built — this one isn't eligible for a reward", cls: "bg-slate-50 text-slate-500 border-slate-200" };
  }
  if (p === "closed") {
    return { label: "Didn't move forward", cls: "bg-slate-50 text-slate-500 border-slate-200" };
  }
  if (p === "scheduled") {
    return { label: "Inspection set — your reward pays after their roof is built", cls: "bg-teal-50 text-bardin-teal border-teal-200" };
  }
  if (p === "contacted") {
    return { label: "We've reached out to them", cls: "bg-orange-50 text-orange-700 border-orange-200" };
  }
  return { label: "Got it — we'll reach out to them soon", cls: "bg-orange-50 text-orange-700 border-orange-200" };
}
